import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, FileText, MessageSquare, Calculator, Settings, LogOut, CalendarOff,
  Menu, X, Bell, ClipboardList, FileEdit, Users, UserPlus, Gauge, Vote,
  CalendarDays, HandHelping, Building, ClipboardCheck, Send, HelpCircle,
  FileCheck, Euro, Truck, Sparkles, Megaphone, Banknote, Gift, Mail, 
  BarChart3, ArrowUpDown, Bug, Building2, ScrollText, ChevronDown, Home,
  Check, Folder, FolderOpen, Phone, Database, Search, History, TrendingUp, Calendar, Download, KeyRound,
  Shield, Trash2, Share2, Star, Image, Wallet, Tag, UserMinus, Landmark, Link2, AlertTriangle,
  ListChecks, Briefcase, Wrench, ShieldCheck, Archive, Bot, Receipt, Inbox, Settings2
} from 'lucide-react';
import PortalFooter from './PortalFooter';
import { KundenInfoLeiste } from './KundenInfoLeiste';
import { PortalBanner } from './PortalBanner';
import { NotificationIcon, kategorieMeta, relativeTime } from './notifications/NotificationItem';
import { syncAppBadge } from '@/lib/push';
import useIosStatusBarTap from '@/hooks/useIosStatusBarTap';
import GlobalSearchInline from './GlobalSearchInline';
import MeetVerwalterButton from './MeetVerwalterButton';
import { SagsSonjaButton, SagsSonjaMobileIcon } from './SagsSonjaButton';
import { OnboardingSubBanner } from './OnboardingSubBanner';
import DemoTourOverlay from './DemoTourOverlay';
import MobileBottomNav from './MobileBottomNav';
import { PullToRefresh } from './PullToRefresh';
import { splashHide } from '@/hooks/usePortalRefresh';
import PwaInstallHint from './PwaInstallHint';
import { KundenansichtBanner } from './KundenansichtBanner';
import { MailDryRunBanner } from './MailDryRunBanner';
import { AbwesenheitBanner } from './AbwesenheitHinweis';
import { isKundenansicht, endKundenansicht } from '@/lib/kundenansicht';
import { useAdminFocusMode } from '@/hooks/useAdminFocusMode';
import { notificationTarget } from '@/utils/notificationTarget';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalLayoutHeader = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const mainRef = useRef(null);
  useIosStatusBarTap(mainRef, !isLoading && !!user);
  // PWA-Startbild erst ausblenden, wenn das Portal wirklich steht (kein weißer Zwischenbildschirm)
  useEffect(() => { if (!isLoading && user) splashHide(); }, [isLoading, user]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const portalMenuRef = useRef(null);
  const userPortalMenuRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);
  // Eigentümer-Menü hängt vom MV/WEG-Status ab → zuletzt bekannten Stand aus dem Cache nehmen, damit das Menü beim Laden nicht „springt“
  const [mvStatus, setMvStatus] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem('user') || 'null'); return u?.id ? JSON.parse(localStorage.getItem(`mv_status_${u.id}`) || 'null') : null; } catch { return null; }
  });
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0);
  const [vsOffenBadge, setVsOffenBadge] = useState(0);
  const [onboardingBadge, setOnboardingBadge] = useState(0);
  const [casaviReportsBadge, setCasaviReportsBadge] = useState(0);
  const { focusMode, setFocusMode } = useAdminFocusMode();

  // Effektive Rolle: Admin im Focus-Modus = Mitarbeiter (kein Spezial-Zugriff)
  const effectiveRole = (focusMode && user?.role === 'admin') ? 'mitarbeiter' : user?.role;
  const isRealAdmin = user?.role === 'admin' || user?.real_role === 'admin';

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const goLogin = () => {
      const target = window.location.pathname + window.location.search;
      if (target.startsWith('/portal')) sessionStorage.setItem('redirect_after_login', target);
      navigate('/login', { replace: true });
    };
    const fetchUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) { if (isMounted) goLogin(); return; }
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401 || res.status === 403) {
          // Token vom Server abgelehnt (abgelaufen/ungültig, z. B. nach Schlüsselwechsel) → sauber neu anmelden,
          // statt mit zwischengespeicherten Nutzerdaten weiterzuarbeiten und überall leere Listen zu zeigen
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          if (isMounted) goLogin();
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (isMounted) { 
          setUser(data); 
          setPermissions(data.permissions || null); 
          localStorage.setItem('user', JSON.stringify(data)); 
          setIsLoading(false); 
        }
      } catch (e) {
        // On API failure, check if we have cached user data with a valid token
        const userData = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        if (userData && token && isMounted) {
          try { 
            // Try to decode the token to check expiration
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              const exp = payload.exp * 1000; // Convert to milliseconds
              if (exp > Date.now()) {
                // Token still valid, use cached user data
                const parsed = JSON.parse(userData); 
                setUser(parsed); 
                setPermissions(parsed.permissions || null); 
                setIsLoading(false);
                return;
              }
            }
            // Token expired or invalid
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            goLogin();
          } catch { 
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            goLogin(); 
          }
        } else if (isMounted) { 
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          goLogin(); 
        }
      }
    };
    fetchUserData();
    return () => { isMounted = false; };
  }, [navigate]);

  // Fetch notifications – zusätzlich sofort bei Push-Eingang (Service-Worker-Nachricht) und Rückkehr in den Tab
  useEffect(() => {
    if (!user) return;
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/notifications/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setNotifications(data);
          const unread = data.filter(n => !n.read).length;
          setUnreadCount(unread);
          syncAppBadge(unread);
        }
      } catch (e) { console.error(e); }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Check every minute
    const onVisible = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
    const onSwMessage = (e) => {
      if (e.data?.type === 'push-received') fetchNotifications();
      if (e.data?.type === 'open-url' && e.data.url) { try { const u = new URL(e.data.url); navigate(u.pathname + u.search); } catch { /* ignorieren */ } }
    };
    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener?.('message', onSwMessage);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener?.('message', onSwMessage);
    };
  }, [user]);

  // Fetch Mietverwaltung status for Eigentümer
  useEffect(() => {
    if (!user || user.role !== 'eigentuemer') return;
    const fetchMvStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/users/me/mietverwaltung-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMvStatus(data);
          try { localStorage.setItem(`mv_status_${user.id}`, JSON.stringify(data)); } catch { /* ignore */ }
        }
      } catch (e) { console.error(e); }
    };
    fetchMvStatus();
  }, [user]);

  // Fetch pending bookings count for Admin/Mitarbeiter
  useEffect(() => {
    if (!user || !['admin', 'mitarbeiter'].includes(user.role)) return;
    const fetchPendingBookings = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/dashboard/offene-themen`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const n = (data.items || []).filter(i => i.key === 'buchungen' || i.key === 'registrierungen').reduce((a, i) => a + (i.count || 0), 0);
          setPendingBookingsCount(n);
        }
      } catch (e) { console.error(e); }
    };
    fetchPendingBookings();
    // Refresh every 2 minutes
    const interval = setInterval(fetchPendingBookings, 120000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch onboarding badge count for all roles
  useEffect(() => {
    if (!user) return;
    const fetchOnboardingBadge = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/onboarding/badge-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOnboardingBadge(data.total || 0);
        }
      } catch (e) { /* silent */ }
    };
    fetchOnboardingBadge();
    const interval = setInterval(fetchOnboardingBadge, 120000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch Casavi-Import-Berichte badge (offene Reports, nur Admin)
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const fetchCasaviBadge = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/schnittstellen/import-reports?only_open=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCasaviReportsBadge(data.open_count || 0);
        }
      } catch (e) { /* silent */ }
    };
    fetchCasaviBadge();
    const interval = setInterval(fetchCasaviBadge, 120000);
    return () => clearInterval(interval);
  }, [user]);

  // Badge: offene Versicherungsrückmeldungen (Staff)
  useEffect(() => {
    if (!user || !['admin', 'mitarbeiter'].includes(user.role)) return;
    const fetchVsBadge = async () => {
      try {
        const res = await fetch(`${API}/api/versicherung/schaeden/offen-count`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (res.ok) setVsOffenBadge((await res.json()).offen || 0);
      } catch (e) { /* silent */ }
    };
    fetchVsBadge();
    const interval = setInterval(fetchVsBadge, 120000);
    return () => clearInterval(interval);
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close nav dropdowns – ignore clicks inside the trigger row OR the portaled menu
      const insideTrigger = dropdownRef.current && dropdownRef.current.contains(e.target);
      const insidePortalMenu = portalMenuRef.current && portalMenuRef.current.contains(e.target);
      if (!insideTrigger && !insidePortalMenu) {
        if (typeof openDropdown === 'number') {
          setOpenDropdown(null);
        }
      }
      // Close user menu (Button-Bereich ODER geportaltes Menü)
      const insideUserPortal = userPortalMenuRef.current && userPortalMenuRef.current.contains(e.target);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target) && !insideUserPortal) {
        if (openDropdown === 'user') {
          setOpenDropdown(null);
        }
      }
      // Close notifications
      if (notificationRef.current && !notificationRef.current.contains(e.target)
          && !(notificationDropdownRef.current && notificationDropdownRef.current.contains(e.target))) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Reposition/close the portaled nav dropdown on resize (button position may change)
  useEffect(() => {
    if (typeof openDropdown !== 'number') return;
    const close = () => setOpenDropdown(null);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [openDropdown]);

  const handleLogout = () => {
    if (isKundenansicht()) { endKundenansicht(); return; }
    // Server-Logout (Musterzugang: setzt Demo-Konto zurück) – Fehler ignorieren, Client meldet sich immer ab
    const t = localStorage.getItem('token');
    if (t) fetch(`${API}/api/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${t}` }, keepalive: true }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Position des Benachrichtigungs-Dropdowns: unter der Glocke (Desktop) bzw. volle Breite (Mobil)
  const notificationDropdownStyle = () => {
    const r = notificationRef.current?.getBoundingClientRect();
    if (!r || window.innerWidth < 640) return { left: 12, right: 12, top: (r ? r.bottom : 56) + 8 };
    return { top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right), width: 380 };
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => { const next = Math.max(0, prev - 1); syncAppBadge(next); return next; });
    } catch (e) { console.error(e); }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/notifications/read-all`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      syncAppBadge(0);
    } catch (e) { console.error(e); }
  };

  // Helper function to sort menu items alphabetically
  const sortMenuItems = (items) => {
    return [...items].sort((a, b) => a.label.localeCompare(b.label, 'de'));
  };

  // Build menu structure
  const buildMenu = () => {
    if (!user) return [];

    // Reine WEG-Eigentümer (nur WEG-Einheiten, kein MV/SEV/eigene Objekte): schlankes Menü
    if (effectiveRole === 'eigentuemer' && mvStatus?.is_pure_weg_owner) {
      const slim = [
        { label: 'Dashboard', path: '/portal/weg-portal', icon: LayoutDashboard },
        { label: 'Vorgänge', path: '/portal/vorgaenge', icon: MessageSquare },
        { label: 'Dokumente', path: '/portal/dokumente', icon: FileText },
        { label: 'Versammlungen', path: '/portal/weg-portal/versammlungen', icon: Calendar },
      ];
      if (mvStatus?.is_beirat) slim.push({ label: 'Beirat', path: '/portal/beirat', icon: ShieldCheck, badge: mvStatus.beirat_pruefungen_offen || 0 });
      return slim;
    }

    // Mieter: schlankes Menü – Kontakt zur Verwaltung läuft über Sonja / Hausmeister
    if (effectiveRole === 'mieter') {
      const slim = [
        { label: 'Dashboard', path: '/portal/mieter-portal', icon: LayoutDashboard },
        { label: 'Vorgänge', path: '/portal/vorgaenge', icon: MessageSquare },
        { label: 'Dokumente', path: '/portal/dokumente', icon: FileText },
        { label: 'Self-Service', path: '/portal/self-service', icon: HandHelping },
        { label: 'Mehr', icon: Home, items: [
          { label: 'Zählerstände', path: '/portal/zaehlerstaende', icon: Gauge },
          { label: 'Termine', path: '/portal/termine', icon: CalendarDays },
          { label: 'Kalender', path: '/portal/kalender', icon: Calendar },
          { label: 'Abstimmungen', path: '/portal/abstimmungen', icon: Vote },
        ] },
      ];
      return slim;
    }

    const menu = [];

    // Dashboard - always visible
    menu.push({ 
      label: 'Dashboard', 
      path: '/portal/dashboard', 
      icon: LayoutDashboard 
    });

    // "Mein Bestand" nur für Eigentümer mit WEG-Einheiten (Sondereigentum, SEV, Versammlungs-Archiv).
    // Reine Miethaus-Eigentümer sehen Objekte, Einheiten & Mieter direkt im Dashboard.
    if (user.role === 'eigentuemer' && mvStatus?.has_weg_units) {
      menu.push({
        label: 'Mein Bestand',
        path: '/portal/mein-bestand',
        icon: Building2,
      });
    }

    // Vorgänge & Dokumente - Eigentümer als Dropdown, Staff als Einzelpunkt
    if (user.role === 'eigentuemer') {
      // Eigentümer: Vorgänge & Dokumente hängen als Unterpunkte in "Objektverwaltung & Termine"
    } else {
      // Admin/Mitarbeiter: "Vorgänge" als einzelner Menüpunkt.
      // "Dokumente" wandert nach "Verwaltung" (schlankeres oberes Menü)
      menu.push({
        label: 'Vorgänge',
        path: '/portal/vorgaenge',
        icon: MessageSquare
      });
    }

    // Objekt & Termine - unterschiedlich je Rolle
    // Für Admin/Mitarbeiter wird "Terminsachen" später nach "Verwaltung" eingefügt
    const objektItems = [];
    
    if (user.role === 'eigentuemer') {
      // Eigentümer: Unterschiedliche Menüs je nach Vertragstyp
      // Vorgänge & Dokumente als erste Unterpunkte (entlastet den Header)
      objektItems.push({ _section: 'Vorgänge & Dokumente', _sectionIdx: 0 });
      objektItems.push({ label: 'Alle Vorgänge', path: '/portal/vorgaenge', icon: MessageSquare });
      objektItems.push({ label: 'Dokumente', path: '/portal/dokumente', icon: FileText });
      objektItems.push({ _section: 'Objektverwaltung', _sectionIdx: 1 });
      // Objekte, Einheiten & Mieter stehen im Dashboard („Ihre Objekte“); „Mein Bestand" nur noch bei WEG-Einheiten
      if (mvStatus?.is_mietverwaltung_kunde) {
        objektItems.push({ label: 'Finanzübersicht', path: '/portal/finanzuebersicht', icon: TrendingUp });
        objektItems.push({ label: 'Objekte-Karteikarte', path: '/portal/objekte-karteikarte', icon: FileText });
        objektItems.push({ label: 'Erfassungsbogen Miethaus', path: '/portal/miethaus-erfassung', icon: ClipboardCheck });
      } else {
        objektItems.push({ label: 'Finanzübersicht', path: '/portal/finanzuebersicht', icon: TrendingUp });
      }
      
      objektItems.push({ label: 'Mein Sondereigentum', path: '/portal/einheiten', icon: Building });
      
      // WEG-Portal für WEG-Eigentümer
      objektItems.push({ label: 'Mein WEG-Portal', path: '/portal/weg-portal', icon: Landmark });
      if (mvStatus?.is_beirat) objektItems.push({ label: 'Beirat – Prüfungen', path: '/portal/beirat', icon: ShieldCheck, badge: mvStatus.beirat_pruefungen_offen || 0 });
      
      // SE-Kunden bekommen "Mieter-Erfassung (SE)"
      objektItems.push({ label: 'Mieter-Erfassung (SE)', path: '/portal/stammblatt', icon: ClipboardCheck });
      
      // SE-Eigentümer (nicht nur MV): Arbeitstools direkt in Objektverwaltung integrieren
      // „Betriebskostenabrechnung“: Standard AUS – nur per Schalter in der Kundenakte oder automatisch bei aktiver SEV
      if (mvStatus?.betriebskosten_anzeigen) {
        objektItems.push({ label: 'Betriebskostenabrechnung', path: '/portal/betriebskosten', icon: Calculator });
      }
      if (!mvStatus?.is_mietverwaltung_kunde) {
        objektItems.push({ label: 'Mietausfall', path: '/portal/mietausfall', icon: Euro });
      }
      
      objektItems.push({ _section: 'Termine', _sectionIdx: 2 });
      objektItems.push({ label: 'Termine', path: '/portal/termine', icon: CalendarDays });
      objektItems.push({ label: 'Kalender', path: '/portal/kalender', icon: Calendar });
      // Abstimmungen nur wenn Mitarbeiter welche eingestellt hat - wird später dynamisch geladen
      
      // Für Eigentümer KEINE Sortierung - feste Reihenfolge
      menu.push({ label: 'Objektverwaltung & Termine', icon: Home, items: objektItems });
    } else {
      // Admin/Mitarbeiter: Alle Optionen - wird später als "Terminsachen" nach "Verwaltung" eingefügt
      objektItems.push({ label: 'Zählerstände', path: '/portal/zaehlerstaende', icon: Gauge });
      objektItems.push({ label: 'Abstimmungen', path: '/portal/abstimmungen', icon: Vote });
      objektItems.push({ label: 'Termine', path: '/portal/termine', icon: CalendarDays });
      objektItems.push({ label: 'Kalender', path: '/portal/kalender', icon: Calendar });
      // NICHT hier pushen - wird nach Verwaltung eingefügt
    }

    // Arbeitstools - nur für Admin/Mitarbeiter oder MV-Eigentümer (SE-Eigentümer haben es in Objektverwaltung)
    const finanzItems = [];
    
    // Betriebskosten für Admin und Mitarbeiter
    if (effectiveRole === 'admin' || effectiveRole === 'mitarbeiter') {
      finanzItems.push({ label: 'Betriebskostenabrechnung', path: '/portal/betriebskosten', icon: Calculator });
    }
    
    if (['admin', 'mitarbeiter'].includes(effectiveRole)) {
      if (effectiveRole === 'admin') {
        finanzItems.push({ label: 'Mietübersicht', path: '/portal/mietuebersicht', icon: Wallet });
      }
      finanzItems.push({ label: 'NK-Anpassung', path: '/portal/nk-anpassung', icon: ArrowUpDown });
      finanzItems.push({ label: 'Mieterhöhung', path: '/portal/mieterhoehung', icon: TrendingUp });
      finanzItems.push({ label: 'Kautionsabrechnung', path: '/portal/kautionsabrechnung', icon: Banknote });
      finanzItems.push({ label: 'Mietbescheinigung', path: '/portal/mietbescheinigung', icon: FileCheck });
      finanzItems.push({ label: 'Quittungen', path: '/portal/quittungen', icon: ClipboardCheck });
      finanzItems.push({ label: 'Kündigungen', path: '/portal/kuendigungen', icon: UserMinus });
      finanzItems.push({ label: 'Mietausfall', path: '/portal/mietausfall', icon: Euro });
    }
    
    // Verwaltung (Admin/Mitarbeiter) - ZUERST
    if (effectiveRole === 'admin' || effectiveRole === 'mitarbeiter') {
      const verwaltungItems = [
        { label: 'Objektverwaltung', path: '/portal/admin/objekte', icon: Building2 },
        { label: 'Kundenverwaltung', path: '/portal/admin/kunden', icon: Users },
        { label: 'Sondereigentumsverwaltung', path: '/portal/admin/sondereigentumsverwaltung', icon: ShieldCheck },
        { label: 'Dokumente', path: '/portal/dokumente', icon: FileText },
        { label: 'Handwerkerverwaltung', path: '/portal/admin/handwerker', icon: Truck },
        { label: 'Wartung & Prüffristen', path: '/portal/admin/prueffristen', icon: Wrench },
        { label: 'Interne Dokumente', path: '/portal/admin/interne-dokumente', icon: FolderOpen },
        { label: 'Vermietungen', path: '/portal/admin/vermietungen', icon: KeyRound },
      ];
      // Admin-only: Muster-Abrechnungen, Eigentümer-Berichte, Angebots-Tool, Neue Buchungen, Registrierungen, Änderungsanträge
      if (effectiveRole === 'admin') {
        verwaltungItems.push({ label: 'Muster-Abrechnungen', path: '/portal/admin/muster-abrechnungen', icon: BarChart3 });
        verwaltungItems.push({ label: 'Eigentümer-Berichte', path: '/portal/admin/eigentuemer-berichte', icon: BarChart3 });
        verwaltungItems.push({ label: 'Angebots-Tool', path: '/portal/admin/angebote', icon: FileText });
      }
      // Neukunden nur für Admin oder Mitarbeiter mit Vollzugriff
      const perms = Array.isArray(user.permissions) ? user.permissions : [];
      if (effectiveRole === 'admin' || perms.includes('vollzugriff') || perms.includes('admin')) {
        verwaltungItems.push({ label: 'Neukunden', path: '/portal/neukunden', icon: ClipboardList });
      }
      if (effectiveRole === 'admin') {
        verwaltungItems.push({ label: 'Änderungsanträge', path: '/portal/admin/aenderungsantraege', icon: FileEdit });
      }
      verwaltungItems.push({ label: 'Kontaktanfragen', path: '/portal/admin/kontaktanfragen', icon: Mail });
      verwaltungItems.push({ label: 'Self-Service-Anfragen', path: '/portal/admin/anfragen', icon: FileText });
      verwaltungItems.push({ label: 'Einladungen & Vorlagen', path: '/portal/einladungen', icon: UserPlus });
      // Ehemalige "Terminsachen"-Unterpunkte in "Verwaltung" integrieren (schlankeres oberes Menü)
      if (objektItems.length > 0) {
        verwaltungItems.push(...objektItems);
      }
      menu.push({ label: 'Verwaltung', icon: Users, items: verwaltungItems });
    }

    // Arbeitstools - nach Verwaltung
    if (finanzItems.length > 0) {
      // Add Aushänge, Kontaktliste/TRH and Mitteilungen to Arbeitstools (nur für Admin/Mitarbeiter)
      if (effectiveRole === 'admin' || effectiveRole === 'mitarbeiter') {
        finanzItems.push(
          { label: 'Wirtschaftsplan & JA', path: '/portal/admin/wirtschaftsplan', icon: Calculator },
          { label: 'Aushänge', path: '/portal/admin/aushang', icon: ScrollText },
          { label: 'Kontaktliste/TRH', path: '/portal/admin/kontaktdaten', icon: Phone },
          { label: 'Eigentümerversammlung', path: '/portal/admin/eigentuemerversammlung', icon: Vote },
          { label: 'Anträge auf Versammlung', path: '/portal/admin/versammlungsantraege', icon: Vote },
          { label: 'Mitteilungen & Rundschreiben', path: '/portal/mitteilungen', icon: Megaphone },
          { label: 'Schriftstücke', path: '/portal/admin/schriftstuecke', icon: FileEdit },
          { label: 'Rechnungen Buchhaltung', path: '/portal/admin/buchhaltung/rechnungen', icon: Receipt }
        );
      }
      // Mitarbeiter: Kennzahlen (Kompaktansicht) über Arbeitstools – Admin hat sie im Admin-Menü unter „Kennzahlen & Berichte"
      if (effectiveRole === 'mitarbeiter') {
        finanzItems.push({ label: 'Kennzahlen', path: '/portal/admin/kennzahlen', icon: BarChart3 });
      }
      // System-Disposition (automatische Beauftragung) für alle Staff-Rollen
      finanzItems.push({ label: 'System-Disposition', path: '/portal/admin/ki-disposition', icon: Bot });
      finanzItems.push({ label: 'Schlüsselbestellungen', path: '/portal/admin/schluesselbestellungen', icon: KeyRound });
      finanzItems.push({ label: 'Versicherungsschäden', path: '/portal/admin/versicherungsschaeden', icon: ShieldCheck, badge: vsOffenBadge });
      finanzItems.push({ label: 'Abwesenheiten & Erreichbarkeit', path: '/portal/admin/abwesenheiten', icon: CalendarOff });
      // Admin-only Items in Arbeitstools: Auftragsformulare, Sonja-Chats, KI-Telefon-Log
      if (effectiveRole === 'admin') {
        finanzItems.push(
          { label: 'Auftragsformulare', path: '/portal/auftragsformulare', icon: FileEdit },
          { label: 'Sonja-Chats', path: '/portal/admin/sonja-chats', icon: MessageSquare },
          { label: 'KI-Telefon-Log', path: '/portal/admin/ki-telefon', icon: Phone },
          { label: 'Vorgangs-Eingang (E-Mail)', path: '/portal/admin/mail-intake', icon: Mail },
        );
      }
      menu.push({ label: 'Arbeitstools', icon: Euro, items: sortMenuItems(finanzItems), badge: vsOffenBadge });
    }

    // "Terminsachen"-Unterpunkte wurden in "Verwaltung" integriert (siehe oben) –
    // kein separater "Terminsachen"-Menüpunkt mehr für Admin/Mitarbeiter.

    // Admin-only
    if (effectiveRole === 'admin') {
      // Admin-Menü gruppiert in logische Sektionen statt einer langen alphabetischen Liste
      const adminSections = [
        {
          title: 'Eingang & Freigaben',
          items: [
            { label: 'Registrierungen & Buchungen', path: '/portal/admin/eingang', icon: Inbox, badge: pendingBookingsCount },
          ],
        },
        {
          title: 'Vorlagen',
          items: [
            { label: 'Vorlagen-Center', path: '/portal/admin/vorlagen', icon: FileText },
          ],
        },
        {
          title: 'Kennzahlen & Berichte',
          items: [
            { label: 'Kennzahlen', path: '/portal/admin/kennzahlen', icon: BarChart3 },
            { label: 'Sonja Analytics', path: '/portal/admin/sonja-analytics', icon: BarChart3 },
            { label: 'Monatsbericht', path: '/portal/admin/monatsbericht', icon: FileText },
          ],
        },
        {
          title: 'WEG-Verwaltung',
          items: [
            { label: 'Eigentümerversammlung', path: '/portal/admin/eigentuemerversammlung', icon: Users },
            { label: 'Anträge auf Versammlung', path: '/portal/admin/versammlungsantraege', icon: Vote },
            { label: 'Hausgeld-Mahnwesen', path: '/portal/admin/hausgeld-mahnwesen', icon: AlertTriangle },
            { label: 'WEG-Funktionen', path: '/portal/admin/weg-features', icon: Settings },
            { label: 'Einheiten-Zuordnung', path: '/portal/admin/einheiten-zuordnung', icon: ListChecks },
            { label: 'Onboarding-Portal', path: '/portal/admin/onboarding', icon: ListChecks, badge: onboardingBadge },
          ],
        },
        {
          title: 'Team & Berechtigungen',
          items: [
            { label: 'Mitarbeiter', path: '/portal/admin/mitarbeiter', icon: Users },
            { label: 'Berechtigungen', path: '/portal/admin/berechtigungen', icon: Shield },
            { label: 'Gewerke-Verwaltung', path: '/portal/admin/gewerke', icon: Tag },
          ],
        },
        {
          title: 'Finanzen & Verträge',
          items: [
            { label: 'Preiskonfiguration', path: '/portal/admin/preise', icon: Euro },
            { label: 'Kostenrechner-Konfiguration', path: '/portal/admin/kostenrechner', icon: Calculator },
            { label: 'BKA-Vorlagen', path: '/portal/admin/bka-templates', icon: FileText },
            { label: 'Mietspiegel-Verwaltung', path: '/portal/admin/mietspiegel', icon: TrendingUp },
            { label: 'Vertragsmanagement', path: '/portal/admin/vertraege', icon: FileEdit },
            { label: 'Vertragseinstellungen', path: '/portal/admin/vertragseinstellungen', icon: FileText },
            { label: 'SEV-Freischaltungen', path: '/portal/admin/sev-freischaltungen', icon: Gift },
            { label: 'Mieterwechsel-Angebote', path: '/portal/admin/mieterwechsel', icon: UserMinus },
            { label: 'E-Mail-Vorlagen', path: '/portal/admin/email-vorlagen', icon: Mail },
            { label: 'Dokumenten-Automation', path: '/portal/admin/dokumenten-automation', icon: Sparkles },
          ],
        },
        {
          title: 'Marketing & Akquise',
          items: [
            { label: 'CMS / Inhalte', path: '/portal/cms', icon: FileEdit },
            { label: 'Marketing-Einstellungen', path: '/portal/admin/marketing-settings', icon: Settings },
            { label: 'KI-Zeiterfassung', path: '/portal/admin/ki-zeiterfassung', icon: Bot },
            { label: 'Werbung Öffentliche Verwaltung', path: '/portal/admin/werbemail-oeffentlich', icon: Mail },
            { label: 'Newsletter', path: '/portal/newsletter', icon: Mail },
            { label: 'Materialien', path: '/portal/admin/materialien', icon: Folder },
            { label: 'Werbematerial', path: '/portal/admin/werbematerial', icon: Share2 },
            { label: 'Social Media Bild', path: '/portal/admin/social-media', icon: Image },
            { label: 'Quartier-Report Leads', path: '/portal/admin/quartier-leads', icon: TrendingUp },
            { label: 'Kostenrechner-Leads', path: '/portal/admin/kostenrechner-leads', icon: Users },
            { label: 'Stellenanzeigen / Karriere', path: '/portal/admin/jobs', icon: Briefcase },
          ],
        },
        {
          title: 'Content & Reputation',
          items: [
            { label: 'Blog verwalten', path: '/portal/admin/blog', icon: FileText },
            { label: 'Blog-Generator (KI)', path: '/portal/admin/blog-generator', icon: Sparkles },
            { label: 'Referenzen', path: '/portal/admin/referenzen', icon: Building },
            { label: 'Bewertungen', path: '/portal/admin/bewertungen', icon: Star },
            { label: 'Vorgangs-Bewertungen', path: '/portal/admin/vorgang-feedback', icon: BarChart3 },
            { label: 'Empfehlungen', path: '/portal/admin/empfehlungen', icon: Gift },
          ],
        },
        {
          title: 'KI & Automation',
          items: [
            { label: 'KI-Wissensbasis', path: '/portal/admin/ki-wissensbasis', icon: Sparkles },
            { label: 'Musterzugang', path: '/portal/admin/musterzugang', icon: UserPlus },
          ],
        },
        {
          title: 'Kommunikation',
          items: [
            { label: 'Terminkalender-Einstellungen', path: '/portal/admin/terminkalender', icon: CalendarDays },
            { label: 'Portal-Posteingang (portal@)', path: '/portal/admin/portal-posteingang', icon: Mail },
            { label: 'Portal-Feedback (Umfrage)', path: '/portal/admin/portal-feedback', icon: Star },
            { label: 'E-Mail-Protokoll', path: '/portal/admin/email-log', icon: Mail },
            { label: 'Sonja-Regeln (feste Stellen)', path: '/portal/admin/sonja-stellen', icon: Bot },
            { label: 'Mail-Vorlagen-Vorschau', path: '/portal/admin/mail-vorschau', icon: Mail },
            { label: 'E-Mail-Einstellungen', path: '/portal/admin/email-einstellungen', icon: Settings },
            { label: 'Mail-Zugangsdaten', path: '/portal/admin/mail-zugangsdaten', icon: Settings },
          ],
        },
        {
          title: 'Sonstiges',
          items: [
            { label: 'Feedback & Bug-Reports', path: '/portal/admin/feedback', icon: Bug },
            { label: 'Partner-Verwaltung', path: '/portal/admin/partner', icon: Users },
            { label: 'Testphase', path: '/portal/admin/testphase', icon: Gift },
          ],
        },
      ];
      // Flatten zu einer Liste mit Section-Markern für das vorhandene Dropdown-Rendering
      const adminItems = [];
      adminSections.forEach((sec, sIdx) => {
        adminItems.push({ _section: sec.title, _sectionIdx: sIdx });
        sortMenuItems(sec.items).forEach((it) => adminItems.push(it));
      });
      menu.push({ label: 'Admin', icon: Settings, items: adminItems, badge: pendingBookingsCount });

      // System-only for Admin (separate from Admin menu)
      const systemItems = [
        { label: 'Schnittstellen', path: '/portal/admin/schnittstellen', icon: Link2, badge: casaviReportsBadge },
        { label: 'E-Mail-Vorschau', path: '/portal/admin/email-vorschau', icon: Mail },
        { label: 'Backups', path: '/portal/admin/backups', icon: Database },
        { label: 'Objekt-Archiv', path: '/portal/admin/objekt-archiv', icon: Archive },
        { label: 'Wartungsfenster', path: '/portal/admin/wartung', icon: Wrench },
        { label: 'Papierkorb', path: '/portal/admin/papierkorb', icon: Trash2 },
        { label: 'Audit-Log', path: '/portal/admin/audit', icon: History },
        { label: 'Exporte', path: '/portal/admin/exporte', icon: Download },
      ];
      menu.push({ label: 'System', icon: Settings, items: systemItems, badge: casaviReportsBadge });
    }

    // Onboarding für Eigentümer/Mieter: nicht im Menü, sondern als Sub-Leiste unter dem Menü (OnboardingSubBanner)
    return menu;
  };

  // Eigentümer ohne bekannten Status: Menü erst zeigen, wenn der Status da ist (statt kurz das falsche Menü)
  const menuItems = user ? (user.role === 'eigentuemer' && mvStatus === null ? [] : buildMenu()) : [];

  const isActive = (path) => location.pathname === path ||
    (path === '/portal/neukunden' && (location.pathname === '/portal/mv-anfragen' || location.pathname === '/portal/serviceanfragen'));

  // Klick auf den bereits aktiven Menüpunkt: mit Unterseite/Filter (?ticket=…, ?status=…) → zurück zur sauberen Übersicht,
  // ohne Parameter (z. B. Dashboard) → Seite neu laden statt nichts zu tun
  const reloadIfSamePage = (e, path) => {
    if (location.pathname !== path) return;
    e.preventDefault();
    if (location.search) { navigate(path, { replace: false }); } else { window.location.reload(); }
  };
  const isCustomer = ['mieter', 'eigentuemer'].includes(user?.role);

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-brand-gold/20 border-t-brand-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="portal-layout" className="portal-app fixed inset-0 bg-gray-50 flex flex-col overflow-hidden">
      {/* Home-Bildschirm-App (iOS/Android): Inhalt reicht unter die Statusleiste – Bereich in Header-Weiß freihalten */}
      <div className="flex-shrink-0 bg-white h-[env(safe-area-inset-top)]" aria-hidden="true" data-testid="pwa-statusbar-spacer" />
      <KundenansichtBanner />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 z-[99990] shadow-sm flex-shrink-0 relative">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-shrink-0 mr-2 sm:mr-4">
              <img src="/images/logo-blau.png" alt="Scholze-Kurz & Kurz" className="h-9 sm:h-11 w-auto flex-shrink-0" data-testid="portal-header-logo" />
              <div className="min-w-0 leading-none block lg:hidden 2xl:block">
                <p className="font-bold text-brand-navy text-[13.5px] sm:text-[16px] leading-tight whitespace-nowrap" data-testid="portal-header-brand">Scholze-Kurz & Kurz</p>
                <p className="text-[9px] sm:text-[10.5px] text-gray-400 tracking-[0.18em] uppercase leading-tight mt-0.5">Kundenportal</p>
              </div>
            </Link>

            {/* Desktop Menu */}
            <nav className="hidden lg:flex items-center gap-1" ref={dropdownRef}>
              {menuItems.map((item, idx) => (
                item.items ? (
                  // Dropdown menu
                  <div key={idx} className="relative">
                    <button
                      onClick={(e) => {
                        if (openDropdown === idx) { setOpenDropdown(null); return; }
                        const r = e.currentTarget.getBoundingClientRect();
                        setDropdownRect({ top: r.bottom + 6, left: r.left });
                        setOpenDropdown(idx);
                      }}
                      className={`flex items-center gap-1.5 px-1.5 2xl:px-2 3xl:px-3 py-2 text-[13px] 3xl:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                        openDropdown === idx || item.items.some(i => isActive(i.path))
                          ? 'bg-brand-navy/5 text-brand-navy'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-brand-navy'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                      {item.badge > 0 && (
                        <span className="ml-1 min-w-[20px] h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5"
                          data-testid={`menu-badge-${item.label.toLowerCase()}`}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === idx ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === idx && dropdownRect && createPortal(
                      <div
                        ref={portalMenuRef}
                        style={{ position: 'fixed', top: dropdownRect.top, left: dropdownRect.left, zIndex: 2147483000 }}
                        className="w-72 bg-white rounded-xl border border-gray-200 shadow-2xl py-2 max-h-[80vh] overflow-y-auto"
                        data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {item.items.map((subItem, subIdx) => (
                          subItem._section ? (
                            <div
                              key={`sec-${subItem._sectionIdx}`}
                              className={`px-4 ${subIdx === 0 ? 'pt-1 pb-1.5' : 'pt-3 pb-1.5 mt-1 border-t border-gray-100'}`}
                            >
                              <p className="text-[10px] font-bold tracking-widest text-brand-gold uppercase">
                                {subItem._section}
                              </p>
                            </div>
                          ) : (
                            <Link
                              key={subIdx}
                              to={subItem.path}
                              onClick={() => setOpenDropdown(null)}
                              data-testid={`menu-link-${subItem.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                              className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                isActive(subItem.path)
                                  ? 'bg-brand-navy/5 text-brand-navy font-medium'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-brand-navy'
                              }`}
                            >
                              <subItem.icon className="w-4 h-4 text-gray-400" />
                              <span className="flex-1">{subItem.label}</span>
                              {subItem.badge > 0 && (
                                <span className="ml-auto min-w-[20px] h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5">
                                  {subItem.badge > 99 ? '99+' : subItem.badge}
                                </span>
                              )}
                            </Link>
                          )
                        ))}
                      </div>,
                      document.body
                    )}
                  </div>
                ) : (
                  // Single link
                  <Link
                    key={idx}
                    to={item.path}
                    onClick={(e) => reloadIfSamePage(e, item.path)}
                    data-testid={`menu-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`flex items-center gap-1.5 px-1.5 2xl:px-2 3xl:px-3 py-2 text-[13px] 3xl:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      isActive(item.path)
                        ? 'bg-brand-navy text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-brand-navy'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                    {item.badge > 0 && (
                      <span className="ml-1 min-w-[20px] h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </Link>
                )
              ))}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* „Sag's Sonja“ – Kunden (Eigentümer + Mieter) schildern ihr Anliegen direkt der Service-Assistentin */}
              {isCustomer && <SagsSonjaButton user={user} />}
              {isCustomer && <SagsSonjaMobileIcon user={user} />}

              {/* Treffen Sie Ihren Verwalter – nur für Eigentümer (Desktop-Pill; mobil über das Nutzer-Menü erreichbar) */}
              {user?.role === 'eigentuemer' && <MeetVerwalterButton />}


              {/* Globale Suche (nur für Verwaltung, Kunden benötigen sie nicht) */}
              {['admin', 'mitarbeiter'].includes(user?.role) && <GlobalSearchInline menu={menuItems} isStaff />}

              {/* Notifications */}
              <div className="relative" ref={notificationRef}>
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-1.5 sm:p-2 text-gray-500 hover:text-brand-navy rounded-lg hover:bg-gray-100 relative"
                  data-testid="notification-bell"
                  aria-label="Benachrichtigungen"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notification-bell-count">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown (Portal: unabhängig von Header-Transform/Overflow, auf Mobil volle Breite) */}
                {showNotifications && createPortal(
                  <div ref={notificationDropdownRef} style={notificationDropdownStyle()} className="fixed bg-white rounded-2xl border border-gray-200 shadow-2xl z-[99999] overflow-hidden" data-testid="notification-dropdown">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/60">
                      <div>
                        <h3 className="font-semibold text-brand-navy text-sm">Benachrichtigungen</h3>
                        <p className="text-[11px] text-gray-500" data-testid="notification-dropdown-unread">{unreadCount > 0 ? `${unreadCount} ungelesen` : 'Alles gelesen'}</p>
                      </div>
                      {unreadCount > 0 && (
                        <button onClick={markAllAsRead} className="text-xs font-medium text-brand-gold hover:underline" data-testid="notification-dropdown-read-all">
                          Alle als gelesen
                        </button>
                      )}
                    </div>
                    <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-400 text-sm">
                          <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          Keine Benachrichtigungen
                        </div>
                      ) : (
                        notifications.slice(0, 10).map((n, i) => (
                          <button
                            type="button"
                            key={n.id}
                            data-testid={`notification-dropdown-item-${i}`}
                            className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!n.read ? 'bg-sky-50/40' : ''}`}
                            onClick={() => {
                              if (!n.read) markAsRead(n.id);
                              navigate(notificationTarget(n));
                              setShowNotifications(false);
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <NotificationIcon n={n} size="sm" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-brand-navy' : 'font-medium text-gray-700'}`}>{n.title}</p>
                                  {!n.read && <span className="w-2 h-2 rounded-full bg-sky-500 flex-shrink-0 mt-1.5" />}
                                </div>
                                <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{n.message}</p>
                                <p className="text-[11px] text-gray-400 mt-1">{kategorieMeta(n).label} · {relativeTime(n.created_at)}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                    <div className="p-2 border-t border-gray-100 flex items-center justify-between gap-2">
                      <Link
                        to="/portal/benachrichtigungen"
                        onClick={() => setShowNotifications(false)}
                        className="flex-1 text-center text-xs font-medium text-brand-navy hover:bg-gray-50 rounded-lg py-2"
                        data-testid="notification-dropdown-all"
                      >
                        Alle anzeigen
                      </Link>
                      <Link
                        to="/portal/benachrichtigungen#push"
                        onClick={() => setShowNotifications(false)}
                        className="flex-1 text-center text-xs font-medium text-brand-gold hover:bg-brand-gold/10 rounded-lg py-2 flex items-center justify-center gap-1"
                        data-testid="notification-dropdown-push"
                      >
                        <Settings2 className="w-3.5 h-3.5" /> Benachrichtigungen verwalten
                      </Link>
                    </div>
                  </div>,
                  document.body
                )}
              </div>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={(e) => {
                    if (openDropdown === 'user') { setOpenDropdown(null); return; }
                    const r = e.currentTarget.getBoundingClientRect();
                    setDropdownRect({ top: r.bottom + 6, right: window.innerWidth - r.right });
                    setOpenDropdown('user');
                  }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid="user-menu-button"
                >
                  <div className={`relative w-8 h-8 rounded-full flex items-center justify-center ${
                    isRealAdmin && focusMode ? 'bg-brand-gold' : 'bg-brand-navy'
                  }`}>
                    {user.avatar_url ? (
                      <img src={`${API}${user.avatar_url}`} alt={user.name || 'Profil'} data-testid="header-avatar-img"
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = 'flex';
                        }} />
                    ) : (
                      <span className="text-white text-xs font-bold">{user.name?.[0] || 'U'}</span>
                    )}
                    {user.avatar_url && (
                      <span className="text-white text-xs font-bold absolute inset-0 items-center justify-center" style={{ display: 'none' }}>{user.name?.[0] || 'U'}</span>
                    )}
                    {isRealAdmin && focusMode && (
                      <span
                        data-testid="admin-focus-mode-indicator"
                        className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-white rounded-full border border-brand-gold flex items-center justify-center"
                        title="Mitarbeiter-Modus aktiv"
                      >
                        <Briefcase className="w-2 h-2 text-brand-gold" />
                      </span>
                    )}
                  </div>
                  <div className="hidden 3xl:block text-left">
                    <p className="text-xs font-semibold text-brand-navy truncate max-w-[140px]">{user.name}</p>
                    <p className="text-[10px] text-gray-400 capitalize">
                      {isRealAdmin && focusMode ? (
                        <span className="text-brand-gold font-medium">Mitarbeiter-Modus</span>
                      ) : (
                        user.role
                      )}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 hidden 3xl:block" />
                </button>

                {openDropdown === 'user' && dropdownRect && createPortal(
                  <div
                    ref={userPortalMenuRef}
                    style={{ position: 'fixed', top: dropdownRect.top, right: dropdownRect.right, zIndex: 2147483000 }}
                    className="w-64 bg-white rounded-xl border border-gray-200 shadow-2xl py-2"
                    data-testid="user-menu-dropdown"
                  >
                    {isRealAdmin && (
                      <>
                        <button
                          onClick={() => { setFocusMode(!focusMode); setTimeout(() => window.location.assign('/portal/dashboard'), 50); }}
                          data-testid="admin-focus-mode-toggle"
                          className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm w-full text-left hover:bg-gray-50 group"
                          title={focusMode ? 'Vollen Admin-Zugriff wieder einschalten' : 'Portal nur mit Mitarbeiter-Menü anzeigen'}
                        >
                          <span className="flex items-center gap-3 min-w-0">
                            <Briefcase className={`w-4 h-4 flex-shrink-0 ${focusMode ? 'text-brand-gold' : 'text-gray-400'}`} />
                            <span className="flex flex-col min-w-0">
                              <span className={`font-medium ${focusMode ? 'text-brand-navy' : 'text-gray-700'}`}>Mitarbeiter-Modus</span>
                              <span className="text-[10px] text-gray-400 leading-tight">
                                {focusMode ? 'Aktiv – Portal wie für Mitarbeiter' : 'Portal exakt wie ein Mitarbeiter sehen'}
                              </span>
                            </span>
                          </span>
                          {/* Switch */}
                          <span
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
                              focusMode ? 'bg-brand-gold' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition mt-0.5 ${
                                focusMode ? 'translate-x-4' : 'translate-x-0.5'
                              }`}
                            />
                          </span>
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                      </>
                    )}
                    {user?.role === 'eigentuemer' && (
                      <Link
                        to="/portal/kontakt-termine"
                        onClick={() => setOpenDropdown(null)}
                        data-testid="user-menu-meet-verwalter"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-brand-navy"
                      >
                        <Phone className="w-4 h-4" />
                        Treffen Sie Ihren Verwalter
                      </Link>
                    )}
                    {['admin', 'mitarbeiter'].includes(user?.role) && (
                      <Link
                        to="/portal/admin/abwesenheiten"
                        onClick={() => setOpenDropdown(null)}
                        data-testid="user-menu-abwesenheit"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-brand-navy"
                      >
                        <CalendarOff className="w-4 h-4" />
                        Abwesenheit melden
                      </Link>
                    )}
                    <Link
                      to="/portal/einstellungen"
                      onClick={() => setOpenDropdown(null)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-brand-navy"
                    >
                      <Settings className="w-4 h-4" />
                      Einstellungen & Verträge
                    </Link>
                    <Link
                      to="/portal/hilfe"
                      onClick={() => setOpenDropdown(null)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-brand-navy"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Hilfe
                    </Link>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full"
                    >
                      <LogOut className="w-4 h-4" />
                      Abmelden
                    </button>
                  </div>,
                  document.body
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-500 hover:text-brand-navy rounded-lg hover:bg-gray-100"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white max-h-[70vh] overflow-y-auto">
            <nav className="p-4 space-y-1">
              {/* Kontakt zum Verwalter – im Mobile-Menü hervorgehoben (nicht für Mieter) */}
              {user?.role !== 'mieter' && (
              <Link
                to="/portal/kontakt-termine"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-meet-verwalter-link"
                className="flex items-center gap-3 px-3 py-3 mb-2 rounded-xl bg-gradient-to-r from-brand-navy to-[#22456e] text-white shadow-sm"
              >
                <span className="w-9 h-9 rounded-full ring-2 ring-brand-gold/70 bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-brand-gold" />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold leading-tight">Treffen Sie Ihren Verwalter</span>
                  <span className="block text-[11px] text-white/70">Kontakt aufnehmen · Termin vereinbaren</span>
                </span>
                <ChevronDown className="w-4 h-4 text-brand-gold -rotate-90" />
              </Link>
              )}

              {menuItems.map((item, idx) => (
                item.items ? (
                  <div key={idx}>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === `mobile-${idx}` ? null : `mobile-${idx}`)}
                      className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50"
                    >
                      <span className="flex items-center gap-2">
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === `mobile-${idx}` ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === `mobile-${idx}` && (
                      <div className="ml-6 mt-1 space-y-1">
                        {item.items.map((subItem, subIdx) => (
                          subItem._section ? (
                            <p
                              key={`m-sec-${subItem._sectionIdx}`}
                              className={`text-[10px] font-bold tracking-widest text-brand-gold uppercase px-3 ${subIdx === 0 ? 'pt-1 pb-0.5' : 'pt-3 pb-0.5'}`}
                            >
                              {subItem._section}
                            </p>
                          ) : (
                            <Link
                              key={subIdx}
                              to={subItem.path}
                              onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}
                              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg ${
                                isActive(subItem.path)
                                  ? 'bg-brand-navy/5 text-brand-navy font-medium'
                                  : 'text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              <subItem.icon className="w-4 h-4" />
                              {subItem.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={idx}
                    to={item.path}
                    onClick={(e) => { setMobileMenuOpen(false); reloadIfSamePage(e, item.path); }}
                    className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg ${
                      isActive(item.path)
                        ? 'bg-brand-navy text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                )
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content - Scrollable area that fills remaining viewport height.
          Kunden (Mieter/Eigentümer): schmale, zentrierte Breite wie Mieter-/WEG-Portal (max-w-5xl); Mitarbeiter: volle Arbeitsbreite. */}
      <main ref={mainRef} className="flex-1 overflow-y-auto font-portal flex flex-col relative" style={{ overscrollBehaviorY: 'contain' }}>
        <PullToRefresh scrollRef={mainRef} />
        {/* Infoleiste der Verwaltung: direkt unter dem Menü, nur für Kunden (nicht für Mitarbeiter) */}
        {isCustomer && <OnboardingSubBanner openCount={onboardingBadge} />}
        {user && !['admin', 'mitarbeiter'].includes(user.role) && <><PortalBanner /><KundenInfoLeiste /><AbwesenheitBanner /></>}
        <MailDryRunBanner user={user} />
        <div className={`${isCustomer ? 'max-w-5xl pb-24 lg:pb-10' : 'max-w-[1600px] pb-10'} mx-auto p-4 lg:p-6 w-full flex-1`} data-testid="portal-main-container">
          <PwaInstallHint />
          {children || <Outlet />}
        </div>
        <PortalFooter />
      </main>

      {/* Mobile App-Navigation (nur Kunden, < lg) */}
      {isCustomer && <MobileBottomNav role={user.role} onMenu={() => setMobileMenuOpen(o => !o)} />}

      {/* Demo-Tour Overlay (für Musterzugang-Nutzer) */}
      <DemoTourOverlay />
    </div>
  );
};

export default PortalLayoutHeader;
